import { jsx as _jsx } from "react/jsx-runtime";
import { App } from "antd";
import { useCallback } from "react";
import { Popconfirm, getRecursivelyCheckedNodes, getTreeParentById, useFolderTree } from "@devowl-wp/react-folder-tree";
import { RML_FOLDER_DELETED } from "../../../events/folderLifecycle.js";
import { useStores } from "../../../store/rootStore.js";
import { __, _i } from "../../../utils/i18n.js";
import { rmlOpts } from "../../../utils/rmlOpts.js";
import { useHandleSelect } from "../useHandleSelect.js";
function useTrashNode() {
    const { message } = App.useApp();
    const { treeStore } = useStores();
    const { set } = useFolderTree();
    const { handleSelect } = useHandleSelect();
    const handleTrash = useCallback(async (_e, node = treeStore.getTreeItemById(treeStore.selectedId), isMulti)=>{
        if (node.childNodes.filter((n)=>n.$visible).length) {
            message.error(__("The folder you try to delete has subfolders."));
            return false;
        }
        const hide = message.loading(_i(__("Deleting {{strong}}%s{{/strong}}...", node.title), {
            strong: /*#__PURE__*/ _jsx("strong", {})
        }));
        try {
            await node.trash();
            document.dispatchEvent(new CustomEvent(RML_FOLDER_DELETED, {
                detail: {
                    id: +node.id
                },
                bubbles: true
            }));
            !isMulti && message.success(_i(__("Successfully deleted {{strong}}%s{{/strong}}", node.title), {
                strong: /*#__PURE__*/ _jsx("strong", {})
            }));
            if (!isMulti) {
                const parentId = getTreeParentById(node.id, treeStore.tree);
                handleSelect(parentId === 0 ? +rmlOpts.others.rootId : parentId);
            }
            return true;
        } catch (e) {
            message.error(e.responseJSON.message);
            return false;
        } finally{
            hide();
        }
    }, [
        treeStore,
        handleSelect,
        message
    ]);
    const handleMultipleTrash = useCallback(async ()=>{
        let count = 0;
        set({
            isTreeBusy: true
        });
        const checkedNodes = getRecursivelyCheckedNodes(treeStore.tree, true);
        for (const nodes of Object.values(checkedNodes).reverse()){
            for (const node of nodes){
                count++;
                const result = await handleTrash(null, node, true);
                if (!result) {
                    set({
                        isTreeBusy: false
                    });
                    return;
                }
            }
        }
        message.success(_i(__("Successfully deleted {{strong}}%d folders{{/strong}}", count), {
            strong: /*#__PURE__*/ _jsx("strong", {})
        }));
        set({
            isTreeBusy: false
        });
        handleSelect(+rmlOpts.others.rootId);
    }, [
        treeStore,
        handleTrash,
        handleSelect,
        message,
        set
    ]);
    const handleTrashModifier = useCallback((body)=>{
        const node = treeStore.getTreeItemById(treeStore.selectedId);
        if (!node) {
            return body;
        }
        return /*#__PURE__*/ _jsx(Popconfirm, {
            placement: "bottom",
            styles: {
                root: {
                    maxWidth: 254
                }
            },
            onConfirm: ()=>handleTrash(undefined, node),
            title: /*#__PURE__*/ _jsx("span", {
                style: {
                    display: "inline-block",
                    maxWidth: 200
                },
                children: _i(__("Are you sure to delete {{strong}}%s{{/strong}}? All files gets moved to Unorganized.", node.title), {
                    strong: /*#__PURE__*/ _jsx("strong", {})
                })
            }),
            okText: __("Ok"),
            cancelText: __("Cancel"),
            children: body
        });
    }, [
        treeStore,
        handleTrash
    ]);
    const handleMultipleTrashModifier = useCallback((body)=>{
        const checkedNodes = getRecursivelyCheckedNodes(treeStore.tree, false);
        return /*#__PURE__*/ _jsx(Popconfirm, {
            placement: "bottom",
            styles: {
                root: {
                    maxWidth: 254
                }
            },
            onConfirm: handleMultipleTrash,
            title: /*#__PURE__*/ _jsx("span", {
                style: {
                    display: "inline-block",
                    maxWidth: 200
                },
                children: _i(__("Are you sure to delete {{strong}}%d folders{{/strong}}? All files gets moved to Unorganized.", checkedNodes.length), {
                    strong: /*#__PURE__*/ _jsx("strong", {})
                })
            }),
            okText: __("Ok"),
            cancelText: __("Cancel"),
            children: body
        });
    }, [
        treeStore,
        handleMultipleTrash
    ]);
    return {
        handleMultipleTrashModifier,
        handleTrashModifier
    };
}
export { useTrashNode };
